import React, { useState, useEffect } from 'react';
import { addDoc, collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';

const OvertimeRequest = ({ db, initials, displayName, setSaveMessage }) => {
    // --- STATE ---
    const [otRequests, setOtRequests] = useState([]);
    const [isOtModalOpen, setIsOtModalOpen] = useState(false);
    const [otType, setOtType] = useState(''); // 'Early Start', 'Reduced Lunch', 'Late Finish'
    
    // Form Inputs
    const [otDate, setOtDate] = useState(''); // Pay Week Commencing
    const [otSpecificDate, setOtSpecificDate] = useState(''); // Specific OT Date
    const [otReason, setOtReason] = useState('');
    
    const [rotaStart, setRotaStart] = useState('');
    const [actualStart, setActualStart] = useState('');
    const [rotaFinish, setRotaFinish] = useState('');
    const [actualFinish, setActualFinish] = useState('');
    const [rotaLunchStart, setRotaLunchStart] = useState('');
    const [rotaLunchEnd, setRotaLunchEnd] = useState('');
    const [actualLunchStart, setActualLunchStart] = useState('');
    const [actualLunchEnd, setActualLunchEnd] = useState('');

    // --- HELPERS ---
    const getPreviousThursday = (date = new Date()) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = (day + 7 - 4) % 7; // 4 is Thursday
        d.setDate(d.getDate() - diff);
        return d.toISOString().split('T')[0];
    };

    // Helper: Force time to nearest 15 minutes
    const snapToQuarter = (timeStr, setFunc) => {
        if (!timeStr) return;
        const [h, m] = timeStr.split(':').map(Number);
        
        // Round minutes to nearest 15
        let roundedM = Math.round(m / 15) * 15;
        let newH = h;

        // Handle rollover (e.g., 60 mins becomes 00 and adds hour)
        if (roundedM === 60) {
            roundedM = 0;
            newH = (newH + 1) % 24;
        }

        // Format back to HH:MM
        const finalH = String(newH).padStart(2, '0');
        const finalM = String(roundedM).padStart(2, '0');
        
        setFunc(`${finalH}:${finalM}`);
    };

    const calcTimeBlock = (startStr, endStr, refDateStr) => {
        if(!startStr || !endStr || !refDateStr) return { totalMins: 0, unsocMins: 0 };
        
        let s = new Date(`${refDateStr}T${startStr}`);
        let e = new Date(`${refDateStr}T${endStr}`);
        
        if(e < s) e.setDate(e.getDate() + 1); // Crosses midnight

        const totalMins = (e - s) / 60000;
        
        // Unsocial calc: overlapping 20:00 (previous day) to 06:00 (current) AND 20:00 (current) to 06:00 (next)
        let unsocMins = 0;
        
        // Check intersection with Unsocial Window 1: Day Start 00:00 to 06:00
        let d1_00 = new Date(s); d1_00.setHours(0,0,0,0);
        let d1_06 = new Date(s); d1_06.setHours(6,0,0,0);
        
        // Check intersection with Unsocial Window 2: Day Start 20:00 to 24:00
        let d1_20 = new Date(s); d1_20.setHours(20,0,0,0);
        let d1_24 = new Date(s); d1_24.setHours(24,0,0,0); 

        // Check intersection with Unsocial Window 3: Next Day 00:00 to 06:00 (if crossing midnight)
        let d2_00 = new Date(s); d2_00.setDate(d2_00.getDate() + 1); d2_00.setHours(0,0,0,0);
        let d2_06 = new Date(s); d2_06.setDate(d2_06.getDate() + 1); d2_06.setHours(6,0,0,0);

        const getOverlap = (start, end, wStart, wEnd) => {
            const maxStart = new Date(Math.max(start, wStart));
            const minEnd = new Date(Math.min(end, wEnd));
            return Math.max(0, (minEnd - maxStart) / 60000);
        };

        unsocMins += getOverlap(s, e, d1_00, d1_06);
        unsocMins += getOverlap(s, e, d1_20, d1_24);
        unsocMins += getOverlap(s, e, d2_00, d2_06);

        return { totalMins, unsocMins };
    };

    // --- FETCHING ---
    const fetchOvertimeRequests = async () => {
        if(!initials) return;
        const q = query(collection(db, "overtime_requests"), where("initials", "==", initials));
        const s = await getDocs(q);
        const data = s.docs.map(d => ({id: d.id, ...d.data()}));
        data.sort((a, b) => {
            const tA = a.timestamp?.seconds || 0;
            const tB = b.timestamp?.seconds || 0;
            return tB - tA;
        });
        setOtRequests(data);
    };

    useEffect(() => {
        fetchOvertimeRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initials]);

    // --- HANDLERS ---
    const handleOpenModal = () => {
        setOtType(''); // Reset the menu selection
        setOtReason('');
        setOtSpecificDate('');
        setOtDate(getPreviousThursday()); // Default to current week
        setRotaStart(''); setActualStart(''); 
        setRotaFinish(''); setActualFinish('');
        setRotaLunchStart(''); setRotaLunchEnd(''); 
        setActualLunchStart(''); setActualLunchEnd('');
        setIsOtModalOpen(true);
    };

    const handleOvertimeSubmit = async () => {
        if(!otDate || !otSpecificDate || !otReason) return alert("Please fill all fields");
        const d = new Date(otDate);
        if(d.getDay() !== 4) return alert("Pay Week must commence on a Thursday.");

        let calculatedMins = 0;
        let calculatedUnsoc = 0;
        let breakdown = "";

        // CALCULATION LOGIC
        if(otType === 'Early Start') {
            if(!actualStart || !rotaStart) return alert("Enter start times");
            const calc = calcTimeBlock(actualStart, rotaStart, otSpecificDate);
            calculatedMins = calc.totalMins;
            calculatedUnsoc = calc.unsocMins;
            breakdown = `Early Start: ${actualStart} to ${rotaStart}`;
        } 
        else if(otType === 'Late Finish') {
            if(!rotaFinish || !actualFinish) return alert("Enter finish times");
            const calc = calcTimeBlock(rotaFinish, actualFinish, otSpecificDate);
            calculatedMins = calc.totalMins;
            calculatedUnsoc = calc.unsocMins;
            breakdown = `Late Finish: ${rotaFinish} to ${actualFinish}`;
        } 
        else if(otType === 'Reduced Lunch') {
            if(!rotaLunchStart || !rotaLunchEnd || !actualLunchStart || !actualLunchEnd) return alert("Enter all lunch times");
            const rCalc = calcTimeBlock(rotaLunchStart, rotaLunchEnd, otSpecificDate);
            const aCalc = calcTimeBlock(actualLunchStart, actualLunchEnd, otSpecificDate);
            calculatedMins = rCalc.totalMins - aCalc.totalMins;
            
            if(rCalc.unsocMins > 0) {
                calculatedUnsoc = calculatedMins;
            } else {
                calculatedUnsoc = 0;
            }
            breakdown = `Reduced Lunch: Taken ${aCalc.totalMins}m (Rota ${rCalc.totalMins}m)`;
        }

        if(calculatedMins < 0) return alert("Calculated time is negative. Check inputs.");

        const hrs = Math.floor(calculatedMins / 60);
        const mins = Math.floor(calculatedMins % 60);
        
        const uHrs = Math.floor(calculatedUnsoc / 60);
        const uMins = Math.floor(calculatedUnsoc % 60);

        await addDoc(collection(db, "overtime_requests"), {
            initials,
            displayName,
            weekCommencing: otDate,
            overtimeDate: otSpecificDate,
            type: otType,
            hours: hrs,
            minutes: mins,
            unsocHours: uHrs,
            unsocMinutes: uMins,
            details: breakdown,
            reason: otReason,
            status: "Pending Approval",
            payrollProcessed: false,
            timestamp: new Date()
        });

        setIsOtModalOpen(false);
        fetchOvertimeRequests();
        setSaveMessage("OT Request Sent");
        setTimeout(() => setSaveMessage(''), 3000);
    };

    const handleDeleteOT = async (id) => {
        if(!window.confirm("Delete request?")) return;
        await deleteDoc(doc(db, "overtime_requests", id));
        fetchOvertimeRequests();
    };

    return (
        <div className="card" style={{maxWidth: '600px', margin: '0 auto', padding:'20px'}}>
            <h3 style={{marginBottom:'15px'}}>Overtime Request</h3>
            
            {/* OVERTIME FORM BUTTON */}
            <div style={{display:'grid', gap:'15px', marginBottom:'30px'}}>
                <button 
                  onClick={handleOpenModal} 
                  style={{width:'100%', padding:'15px', fontSize:'16px', background:'var(--primary)', color:'white'}}
                >
                    + Create New Request
                </button>
            </div>

            {/* PENDING REQUESTS LIST */}
            <div style={{marginTop:'25px', borderTop:'1px solid var(--border)', paddingTop:'15px'}}>
                <h4 style={{fontSize:'14px', marginBottom:'10px'}}>My History</h4>
                {otRequests.length === 0 ? (
                    <p className="text-sm" style={{fontStyle:'italic'}}>No requests found.</p>
                ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
                        {otRequests.map(req => {
                            let statusColor = 'var(--warning)';
                            if (req.status === 'Approved') statusColor = 'var(--success)';
                            if (req.status === 'Denied') statusColor = 'var(--danger)';

                            return (
                                <div key={req.id} style={{background:'var(--light)', padding:'10px', borderRadius:'8px', fontSize:'13px', display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                    <div>
                                        <div style={{fontWeight:'bold'}}>W/C {req.weekCommencing} <span style={{fontWeight:'normal', color:'var(--text-muted)'}}>({req.overtimeDate})</span></div>
                                        <div style={{fontSize:'12px', fontWeight:'600', color:'var(--primary)', marginTop:'2px'}}>{req.type}</div>
                                        <div>{req.details}</div>
                                        <div style={{color:'var(--text-muted)', fontSize:'12px', marginTop:'2px'}}>"{req.reason}"</div>
                                        {req.status === 'Denied' && req.managerNote && (
                                            <div style={{color:'var(--danger)', fontSize:'12px', fontWeight:'bold', marginTop:'4px'}}>Reason: {req.managerNote}</div>
                                        )}
                                        {(req.hours > 0 || req.minutes > 0) && <div style={{marginTop:'4px', fontWeight:'bold'}}>Total: {req.hours}h {req.minutes}m <span style={{color:'var(--text-muted)', fontWeight:'normal'}}>(Unsoc: {req.unsocHours}h {req.unsocMinutes}m)</span></div>}
                                    </div>
                                    <div style={{textAlign:'right'}}>
                                        <span className="badge" style={{background: statusColor, color: 'white'}}>{req.status}</span>
                                        {req.status === 'Pending Approval' && (
                                            <div style={{marginTop:'5px', cursor:'pointer', color:'var(--danger)', fontSize:'11px', fontWeight:'bold'}} onClick={()=>handleDeleteOT(req.id)}>Cancel</div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* MODAL */}
            {isOtModalOpen && (
                <div className="popup-overlay">
                    <div className="card animate-fade-in" style={{width:'90%', maxWidth:'450px', padding:'20px'}}>
                        <h3 style={{marginTop:0, marginBottom:'20px', textAlign:'center'}}>New OT Request</h3>
                        
                        {/* STEP 1: SELECT TYPE */}
                        {!otType && (
                            <div style={{display:'grid', gap:'15px'}}>
                                <button onClick={()=>setOtType('Early Start')} style={{padding:'15px', fontWeight:'bold', background:'rgba(94,106,210,0.12)', border:'1px solid rgba(94,106,210,0.45)', color:'#a5b0f0'}}>Early Start</button>
                                <button onClick={()=>setOtType('Reduced Lunch')} style={{padding:'15px', fontWeight:'bold', background:'rgba(217,119,6,0.12)', border:'1px solid rgba(217,119,6,0.45)', color:'#fbbf24'}}>Reduced Lunch</button>
                                <button onClick={()=>setOtType('Late Finish')} style={{padding:'15px', fontWeight:'bold', background:'rgba(27,143,90,0.12)', border:'1px solid rgba(27,143,90,0.45)', color:'#4ade80'}}>Late Finish</button>
                                <button onClick={()=>setIsOtModalOpen(false)} style={{marginTop:'10px', background:'var(--light)', color:'var(--text-main)', border:'1px solid var(--border)'}}>Cancel</button>
                            </div>
                        )}

                        {/* STEP 2: SPECIFIC INPUTS */}
                        {otType && (
                            <div style={{display:'grid', gap:'15px'}}>
                                <div style={{textAlign:'center', fontWeight:'bold', marginBottom:'10px', color:'var(--primary)'}}>{otType}</div>
                                
                                <div>
                                    <label style={{display:'block', fontSize:'11px', fontWeight:'bold'}}>Specific OT Date:</label>
                                    <input 
                                      type="date" 
                                      value={otSpecificDate} 
                                      onChange={e => {
                                          const val = e.target.value;
                                          setOtSpecificDate(val);
                                          if(val) setOtDate(getPreviousThursday(val));
                                      }} 
                                    />
                                </div>
                                <div>
                                    <label style={{display:'block', fontSize:'11px', fontWeight:'bold', color:'var(--text-muted)'}}>Pay Week Commencing (Thu):</label>
                                    <input type="date" value={otDate} disabled style={{background:'var(--input-bg)', opacity:0.7}} />
                                </div>

                                {otType === 'Early Start' && (
                                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                                        <div>
                                            <label style={{fontSize:'11px'}}>Rota Start</label>
                                            <input 
                                                type="time" 
                                                step="900" 
                                                value={rotaStart} 
                                                onChange={e=>setRotaStart(e.target.value)} 
                                                onBlur={e=>snapToQuarter(e.target.value, setRotaStart)}
                                            />
                                        </div>
                                        <div>
                                            <label style={{fontSize:'11px'}}>Actual Start</label>
                                            <input 
                                                type="time" 
                                                step="900" 
                                                value={actualStart} 
                                                onChange={e=>setActualStart(e.target.value)}
                                                onBlur={e=>snapToQuarter(e.target.value, setActualStart)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {otType === 'Late Finish' && (
                                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                                        <div>
                                            <label style={{fontSize:'11px'}}>Rota Finish</label>
                                            <input 
                                                type="time" 
                                                step="900" 
                                                value={rotaFinish} 
                                                onChange={e=>setRotaFinish(e.target.value)}
                                                onBlur={e=>snapToQuarter(e.target.value, setRotaFinish)}
                                            />
                                        </div>
                                        <div>
                                            <label style={{fontSize:'11px'}}>Actual Finish</label>
                                            <input 
                                                type="time" 
                                                step="900" 
                                                value={actualFinish} 
                                                onChange={e=>setActualFinish(e.target.value)}
                                                onBlur={e=>snapToQuarter(e.target.value, setActualFinish)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {otType === 'Reduced Lunch' && (
                                    <>
                                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                                            <div>
                                                <label style={{fontSize:'11px'}}>Rota Lunch Start</label>
                                                <input 
                                                    type="time" 
                                                    step="900" 
                                                    value={rotaLunchStart} 
                                                    onChange={e=>setRotaLunchStart(e.target.value)}
                                                    onBlur={e=>snapToQuarter(e.target.value, setRotaLunchStart)}
                                                />
                                            </div>
                                            <div>
                                                <label style={{fontSize:'11px'}}>Rota Lunch End</label>
                                                <input 
                                                    type="time" 
                                                    step="900" 
                                                    value={rotaLunchEnd} 
                                                    onChange={e=>setRotaLunchEnd(e.target.value)}
                                                    onBlur={e=>snapToQuarter(e.target.value, setRotaLunchEnd)}
                                                />
                                            </div>
                                        </div>
                                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px'}}>
                                            <div>
                                                <label style={{fontSize:'11px'}}>Actual Lunch Start</label>
                                                <input 
                                                    type="time" 
                                                    step="900" 
                                                    value={actualLunchStart} 
                                                    onChange={e=>setActualLunchStart(e.target.value)}
                                                    onBlur={e=>snapToQuarter(e.target.value, setActualLunchStart)}
                                                />
                                            </div>
                                            <div>
                                                <label style={{fontSize:'11px'}}>Actual Lunch End</label>
                                                <input 
                                                    type="time" 
                                                    step="900" 
                                                    value={actualLunchEnd} 
                                                    onChange={e=>setActualLunchEnd(e.target.value)}
                                                    onBlur={e=>snapToQuarter(e.target.value, setActualLunchEnd)}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div>
                                    <label style={{display:'block', fontSize:'11px', fontWeight:'bold'}}>Reason:</label>
                                    <textarea value={otReason} onChange={e=>setOtReason(e.target.value)} style={{width:'100%', height:'60px', padding:'8px', boxSizing:'border-box'}} />
                                </div>

                                <div className="flex-gap">
                                    <button onClick={()=>handleOpenModal()} style={{background:'var(--light)', color:'var(--text-main)', flex:1}}>Back</button>
                                    <button onClick={handleOvertimeSubmit} style={{background:'var(--primary)', color:'white', flex:1}}>Submit</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default OvertimeRequest;